import Phaser from 'phaser';
import { MenuNavigation } from '@/systems/MenuNavigation';
import { PauseMenuState } from '@/systems/PauseMenuState';
import { buildPauseMenuItems } from '@/data/menuConfig';
import { getSaveManager, ACTIVE_SLOT, DEFAULT_OPTIONS } from '@/systems/SaveManager';
import { OPTION_VOLUME_STEP } from '@/systems/OptionsMenuActions';
import { OptionsMenuForm } from '@/scenes/OptionsMenuForm';

const FONT = '"Courier New", monospace';
const OVERLAY_DEPTH = 1000;
const MENU_DEPTH = 1001;

/**
 * PauseScene — lightweight overlay rendered above StageScene.
 *
 * Lifecycle:
 *   StageScene.create() binds ESC → scene.pause() + scene.launch('PauseScene').
 *   PauseScene handles input; on Resume it calls scene.resume('StageScene').
 *   On Restart/Quit it tears down HudScene + StageScene before navigating away.
 *
 * TODO(MY-1455): implement full key-capture flow for KEY REMAP + GAMEPAD REMAP.
 * TODO(MY-1455): read screenShakeEnabled from saved options in StageScene camera effects.
 * TODO(MY-1455): read bloodEnabled from saved options in Combat damage effects.
 */
export class PauseScene extends Phaser.Scene {
  private state!: PauseMenuState;
  private pauseNav!: MenuNavigation;
  private optsForm!: OptionsMenuForm;

  // ── Pause-screen objects ───────────────────────────────────────────────────
  private pauseGroup!: Phaser.GameObjects.Group;
  private pauseTexts: Phaser.GameObjects.Text[] = [];
  private pauseCursor!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'PauseScene' });
  }

  create(): void {
    const { width, height } = this.scale;

    const slot = getSaveManager().load(ACTIVE_SLOT);
    const savedOpts = slot?.options ?? { ...DEFAULT_OPTIONS };
    this.state = new PauseMenuState(savedOpts);
    this.pauseNav = new MenuNavigation(buildPauseMenuItems());
    this.optsForm = new OptionsMenuForm(this, this.state, MENU_DEPTH);

    // Semi-transparent overlay.
    this.add
      .rectangle(width / 2, height / 2, width, height, 0x000000, 0.72)
      .setDepth(OVERLAY_DEPTH);

    this.buildPauseScreen(width, height);
    this.optsForm.build(width, height);

    this.showPauseScreen();
    this.bindKeys();
  }

  // ── Screen builders ────────────────────────────────────────────────────────

  private buildPauseScreen(width: number, height: number): void {
    this.pauseGroup = this.add.group();

    const title = this.add
      .text(width / 2, height * 0.3, 'PAUSED', {
        fontFamily: FONT,
        fontSize: '48px',
        color: '#ef233c',
        stroke: '#000000',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(MENU_DEPTH);
    this.pauseGroup.add(title);

    const items = this.pauseNav.items;
    const startY = height * 0.46;
    const step = 60;

    this.pauseTexts = items.map((item, i) => {
      const t = this.add
        .text(width / 2, startY + i * step, item.label, {
          fontFamily: FONT,
          fontSize: '30px',
          color: '#888888',
          letterSpacing: 3,
        })
        .setOrigin(0.5)
        .setDepth(MENU_DEPTH);
      this.pauseGroup.add(t);
      return t;
    });

    this.pauseCursor = this.add
      .text(0, 0, '▶', {
        fontFamily: FONT,
        fontSize: '22px',
        color: '#ef233c',
      })
      .setOrigin(1, 0.5)
      .setDepth(MENU_DEPTH);
    this.pauseGroup.add(this.pauseCursor);
  }

  // ── Show / hide ────────────────────────────────────────────────────────────

  private showPauseScreen(): void {
    this.optsForm.setVisible(false);
    this.pauseGroup.setVisible(true);
    this.syncPauseCursor();
  }

  private showOptsScreen(): void {
    this.pauseGroup.setVisible(false);
    this.optsForm.setVisible(true);
    this.optsForm.refreshValues();
  }

  // ── Sync helpers ───────────────────────────────────────────────────────────

  private syncPauseCursor(): void {
    const idx = this.pauseNav.index;
    this.pauseTexts.forEach((t, i) => {
      if (i === idx) {
        t.setColor('#ffffff').setScale(1.05);
      } else {
        t.setColor('#888888').setScale(1);
      }
    });
    const sel = this.pauseTexts[idx];
    if (sel) {
      this.pauseCursor.setPosition(sel.x - sel.displayWidth / 2 - 18, sel.y);
    }
  }

  // ── Key binding ────────────────────────────────────────────────────────────

  private bindKeys(): void {
    const kb = this.input.keyboard!;
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP).on('down', () => this.onUp());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN).on('down', () => this.onDown());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT).on('down', () => this.onLeft());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT).on('down', () => this.onRight());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.W).on('down', () => this.onUp());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.S).on('down', () => this.onDown());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.A).on('down', () => this.onLeft());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.D).on('down', () => this.onRight());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER).on('down', () => this.onConfirm());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE).on('down', () => this.onConfirm());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.ESC).on('down', () => this.onBack());
  }

  // ── Input handlers ─────────────────────────────────────────────────────────

  private onUp(): void {
    if (this.state.screen === 'pause') {
      this.pauseNav.moveUp();
      this.syncPauseCursor();
    } else {
      this.optsForm.moveUp();
    }
  }

  private onDown(): void {
    if (this.state.screen === 'pause') {
      this.pauseNav.moveDown();
      this.syncPauseCursor();
    } else {
      this.optsForm.moveDown();
    }
  }

  private onLeft(): void {
    if (this.state.screen !== 'options') return;
    this.optsForm.adjust(-OPTION_VOLUME_STEP);
  }

  private onRight(): void {
    if (this.state.screen !== 'options') return;
    this.optsForm.adjust(OPTION_VOLUME_STEP);
  }

  private onConfirm(): void {
    if (this.state.screen === 'pause') {
      this.handlePauseConfirm();
    } else {
      this.handleOptsConfirm();
    }
  }

  private onBack(): void {
    if (this.state.screen === 'options') {
      this.persistOptions();
      this.state.exitOptions();
      this.showPauseScreen();
    } else {
      this.doResume();
    }
  }

  // ── Pause menu actions ─────────────────────────────────────────────────────

  private handlePauseConfirm(): void {
    const id = this.pauseNav.confirm();
    switch (id) {
      case 'resume':
        this.doResume();
        break;
      case 'restart':
        this.doRestart();
        break;
      case 'options':
        this.state.enterOptions();
        this.showOptsScreen();
        break;
      case 'quit-to-title':
        this.doQuitToTitle();
        break;
      default:
        break;
    }
  }

  private doResume(): void {
    this.scene.stop('PauseScene');
    this.scene.resume('StageScene');
  }

  private doRestart(): void {
    this.scene.stop('PauseScene');
    this.scene.stop('HudScene');
    this.scene.start('StageScene');
  }

  private doQuitToTitle(): void {
    this.scene.stop('PauseScene');
    this.scene.stop('HudScene');
    this.scene.stop('StageScene');
    this.scene.start('TitleScene');
  }

  // ── Options menu actions ───────────────────────────────────────────────────

  private handleOptsConfirm(): void {
    const result = this.optsForm.confirm();
    if (result.back) {
      this.persistOptions();
      this.state.exitOptions();
      this.showPauseScreen();
    }
  }

  private persistOptions(): void {
    getSaveManager().saveOptions(ACTIVE_SLOT, { ...this.state.opts }, Date.now());
  }
}
